import {
  SOURCE_ID_ALIASES,
  SOURCE_ID_HEADER_LIMIT,
  SOURCE_ID_HEADER_PRIORITY,
  SOURCE_ID_LABELS,
  SOURCE_ID_SHORT_LABELS,
} from '@/features/patients/constants'
import { titleCase } from '@/lib/utils'

export interface SourceIdEntry {
  /** Canonical system key, e.g. 'ecw_client_id'. */
  system: string
  /** Full display name for the Identifiers tab. */
  label: string
  /** Compact display name for the header chips. */
  shortLabel: string
  value: string
}

/**
 * Fold a raw source_ids key onto its canonical form. Keys arrive spelled
 * several ways for one system ('ECW', 'ecw_client_id', 'Safety Net'); without
 * this, a client carrying two spellings lists the same system twice under two
 * different names.
 */
export function canonicalSourceIdKey(key: string): string {
  const lower = key.trim().toLowerCase().replace(/\s+/g, ' ')
  return SOURCE_ID_ALIASES[lower] ?? lower.replace(/ /g, '_')
}

const CLIENT_ID_SUFFIX = '_client_id'

/**
 * Labels for a system with none of its own. A "<system>_client_id" key, the
 * shape every source's client id arrives in, is named after its system: short
 * names read as acronyms ("ccah_client_id" → "CCAH"), longer ones are
 * title-cased. Without this the table badge spends its width on "Ccah Client
 * Id" and has none left for the id itself.
 */
function fallbackLabels(system: string): { label: string; shortLabel: string } {
  if (system.endsWith(CLIENT_ID_SUFFIX) && system !== CLIENT_ID_SUFFIX) {
    const prefix = system.slice(0, -CLIENT_ID_SUFFIX.length)
    const name =
      prefix.length <= 4 && !prefix.includes('_')
        ? prefix.toUpperCase()
        : titleCase(prefix)
    return { label: `${name} client ID`, shortLabel: name }
  }
  return { label: titleCase(system), shortLabel: titleCase(system) }
}

/**
 * Canonicalize, de-alias and de-duplicate a source_ids map into display rows.
 *
 * Two keys folding onto the same system are collapsed only when they carry the
 * same value — a genuine second identifier for that system is kept, since the
 * alternative is silently hiding an id someone needs to reconcile against.
 */
export function mergeSourceIds(
  sourceIds?: Record<string, string>
): SourceIdEntry[] {
  const seen = new Set<string>()
  const entries: SourceIdEntry[] = []

  for (const [rawKey, rawValue] of Object.entries(sourceIds ?? {})) {
    const value = String(rawValue ?? '').trim()
    if (!value) continue

    const system = canonicalSourceIdKey(rawKey)
    const dedupeKey = `${system}:${value}`
    if (seen.has(dedupeKey)) continue
    seen.add(dedupeKey)

    entries.push({
      system,
      label: SOURCE_ID_LABELS[system] ?? fallbackLabels(system).label,
      shortLabel:
        SOURCE_ID_SHORT_LABELS[system] ?? fallbackLabels(system).shortLabel,
      value,
    })
  }

  // Ranked systems first, in priority order; anything unranked keeps its
  // original relative order behind them.
  return entries.toSorted((a, b) => {
    const rankA = SOURCE_ID_HEADER_PRIORITY.indexOf(a.system)
    const rankB = SOURCE_ID_HEADER_PRIORITY.indexOf(b.system)
    if (rankA === rankB) return 0
    if (rankA === -1) return 1
    if (rankB === -1) return -1
    return rankA - rankB
  })
}

export interface HeaderSourceIds {
  shown: SourceIdEntry[]
  /** How many identifiers the header is not showing. */
  remaining: number
  total: number
}

/** The identifiers that earn a header chip, plus the count left for the tab. */
export function headerSourceIds(
  sourceIds?: Record<string, string>,
  limit: number = SOURCE_ID_HEADER_LIMIT
): HeaderSourceIds {
  const entries = mergeSourceIds(sourceIds)
  const shown = entries.slice(0, limit)
  return {
    shown,
    remaining: Math.max(entries.length - shown.length, 0),
    total: entries.length,
  }
}

/**
 * Estimated width of one identifier badge, in CSS pixels.
 *
 * Calibrated against rendered badges at the table's 12px/500 weight with
 * `px-2` padding and a `gap-1` between label and value: "MRN 10001" measures
 * 84.5px, "CIN 98K41207X" 110.4px, a bare "+4" 33.6px. Character width is not
 * uniform — this font gives digits no tabular advance, so two same-length
 * values differ by a couple of pixels — hence a mean per-character figure and
 * a small margin, biased to overestimate. Overestimating drops a badge that
 * would just have fitted; underestimating overflows the cell, which is the
 * worse of the two in a table row.
 */
const BADGE_HORIZONTAL_PADDING = 18
const BADGE_LABEL_GAP = 4
const BADGE_CHAR_WIDTH = 7.8
const BADGE_GAP = 4
const BADGE_MARGIN = 2

function badgeWidth(label: string, value: string): number {
  const chars = label.length + value.length
  return (
    BADGE_HORIZONTAL_PADDING +
    (label ? BADGE_LABEL_GAP : 0) +
    chars * BADGE_CHAR_WIDTH +
    BADGE_MARGIN
  )
}

export interface PackedSourceIds {
  shown: SourceIdEntry[]
  /** Identifiers that did not fit; 0 when everything is shown. */
  remaining: number
}

/**
 * As many identifiers as fit the cell, with the rest counted.
 *
 * The overflow badge has to fit too, so the pack is tried longest-first: a
 * second identifier only earns its place if it and a "+N" alongside it still
 * clear the width. At least one is always returned — a single identifier too
 * wide for the cell truncates rather than collapsing the column to a count.
 */
export function packSourceIds(
  entries: SourceIdEntry[],
  availableWidth: number
): PackedSourceIds {
  if (entries.length === 0) return { shown: [], remaining: 0 }

  const widths = entries.map((entry) =>
    badgeWidth(entry.shortLabel, entry.value)
  )

  for (let count = entries.length; count > 1; count--) {
    const overflow = entries.length - count
    let total = widths.slice(0, count).reduce((sum, width) => sum + width, 0)
    total += BADGE_GAP * (count - 1)
    if (overflow > 0) {
      total += BADGE_GAP + badgeWidth('', `+${overflow}`)
    }
    if (total <= availableWidth) {
      return { shown: entries.slice(0, count), remaining: overflow }
    }
  }

  return { shown: entries.slice(0, 1), remaining: entries.length - 1 }
}
